import React, { useMemo, useState, useEffect, useRef } from "react";
import Button from "../components/Button/Button";
import { InputText } from "primereact/inputtext";
import { Edit, Save, RefreshCw } from "lucide-react";
import ReusableTable from "../components/Table/ReusableTable";
import CustomDialog from "../components/dialog/CustomDialog";
import { get, post, put } from "../utils/api";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";

const TransactionManagement = () => {
  const [loading, setLoading] = useState(false);
  const [validTransactions, setValidTransactions] = useState([]);
  const [errorTransactions, setErrorTransactions] = useState([]);
  const [rejectedTransactions, setRejectedTransactions] = useState([]);
  const [editingTx, setEditingTx] = useState(null);
  const [selectedTab, setSelectedTab] = useState("valid");
  const [placementFeePercent, setPlacementFeePercent] = useState("0");
  const [isEditingError, setIsEditingError] = useState(false);
  const [editSource, setEditSource] = useState(null); // New state for source
  const [amcUsers, setAmcUsers] = useState([]);
  const [selectedAmcUser, setSelectedAmcUser] = useState("");
  const [transactionCheckers, setTransactionCheckers] = useState({});

  const uploadCalled = useRef(false);

  const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
  const userId = userDetails?.id || userDetails?.user_Id;

  // Fetch AMC users
  useEffect(() => {
    const fetchAmcUsers = async () => {
      try {
        const response = await get("/transactionmanagement/amc-users");
        if (response.data) {
          setAmcUsers(response.data);
        }
      } catch (error) {
        console.error("Error fetching AMC users:", error);
      }
    };

    fetchAmcUsers();
  }, []);

  useEffect(() => {
    if (uploadCalled.current) return;
    uploadCalled.current = true;
    processKfinFile();
  }, []);

  const processKfinFile = async () => {
    try {
      setLoading(true);
      const response = await post("/transactionmanagement/upload-kfin-file");

      if (response.data && response.data.insertedRows !== undefined) {
        if (response.data.insertedRows === 0) {
          console.log("No new data to insert.");
        } else {
          console.log(
            `${response.data.insertedRows} valid rows inserted successfully.`
          );
        }
      }
      await fetchTransactions();
    } catch (error) {
      console.error("Failed to process KFin file:", error);
      if (error.response?.status !== 404 && error.response?.status !== 400) {
        console.error("Error details:", error.response?.data?.message);
      }
      await fetchTransactions();
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      const [validResponse, errorResponse, rejectedResponse] = await Promise.all([
        get("/transactionmanagement/kfin-transactions?is_correct=true"),
        get("/transactionmanagement/kfin-transactions?is_correct=false"),
        get("/transactionmanagement/fetchRejectTransactions")
      ]);

      console.log("Valid Response:", validResponse);
      console.log("Error Response:", errorResponse);
      console.log("Rejected Response:", rejectedResponse);

      // For valid and error transactions
      setValidTransactions(validResponse.data || []);
      setErrorTransactions(errorResponse.data || []);

      // Handle rejected transactions response
      // Access as rejectedResponse.data.Data (note: capital D)
      if (rejectedResponse && rejectedResponse.data && rejectedResponse.data.Success) {
        // The Data array is inside rejectedResponse.data.Data
        const rejectedData = rejectedResponse.data.Data || [];
        console.log("Rejected Data:", rejectedData);
        setRejectedTransactions(rejectedData);
      } else {
        console.log("No rejected data found or invalid response structure");
        setRejectedTransactions([]);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      alert("Unable to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectedTransactions = async () => {
    try {
      const response = await get("/transactionmanagement/fetchRejectTransactions");

      console.log("Fetch Rejected Response:", response);

      // Handle response structure: response.data.Data
      if (response && response.data && response.data.Success && response.data.Data) {
        setRejectedTransactions(response.data.Data || []);
      } else {
        setRejectedTransactions([]);
      }
    } catch (error) {
      console.error("Failed to fetch rejected transactions:", error);
    }
  };
  // Helper function to convert to number safely
  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  // Function to calculate fees based on placement fee percentage
  const calculateFees = (placementPercent, capitalCommitment) => {
    const capitalAmount = toNumber(capitalCommitment);
    const placementPercentValue = toNumber(placementPercent);

    const placementFeeAmount = (capitalAmount * placementPercentValue) / 100;
    const upfrontFeePercent = placementPercentValue / 3;
    const upfrontFeeAmount = placementFeeAmount / 3;
    const trailPercent = (placementPercentValue * 2) / 3;
    const trailAmount = (placementFeeAmount * 2) / 3;

    return {
      placement_fee_per: parseFloat(placementPercentValue.toFixed(2)),
      placement_fee_amount: placementFeeAmount,
      upfront_fee_per: parseFloat(upfrontFeePercent.toFixed(2)),
      upfront_fee_amount: upfrontFeeAmount,
      trail_per: parseFloat(trailPercent.toFixed(2)),
      trail_amount: trailAmount
    };
  };

  const handleEditClick = (transaction, source) => {
    setEditingTx(transaction);
    setEditSource(source);
    setIsEditingError(source === 'error');
    setPlacementFeePercent(transaction.placement_fee_per?.toString() || "0");
    const checkerForTx = transactionCheckers[transaction.id] || transaction.checker_name || "";
    setSelectedAmcUser(checkerForTx);
  };

  const handlePlacementFeeChange = (value) => {
    const cleanedValue = value.replace(/[^0-9.]/g, '');
    const parts = cleanedValue.split('.');
    if (parts.length > 2) {
      return;
    }

    setPlacementFeePercent(cleanedValue);

    if (editingTx) {
      const capitalCommitment = editingTx.capital_commitment || 0;
      const calculatedFees = calculateFees(cleanedValue, capitalCommitment);

      setEditingTx({
        ...editingTx,
        placement_fee_per: calculatedFees.placement_fee_per,
        placement_fee_amount: calculatedFees.placement_fee_amount,
        upfront_fee_per: calculatedFees.upfront_fee_per,
        upfront_fee_amount: calculatedFees.upfront_fee_amount,
        trail_per: calculatedFees.trail_per,
        trail_amount: calculatedFees.trail_amount,
        isEdited: true
      });
    }
  };

  const handleFieldChange = (field, value) => {
    if (!editingTx) return;

    setEditingTx(prev => ({
      ...prev,
      [field]: value,
      isEdited: true
    }));
  };

  const handleCheckerChange = (transactionId, checkerName) => {
    setTransactionCheckers(prev => ({
      ...prev,
      [transactionId]: checkerName
    }));

    if (editingTx && editingTx.id === transactionId) {
      setSelectedAmcUser(checkerName);
    }
  };

  const handleSave = async () => {
    if (!editingTx) return;

    try {
      setLoading(true);

      const checkerName = transactionCheckers[editingTx.id] || selectedAmcUser || "";

      if (!checkerName) {
        alert("Please select a checker name before saving");
        setLoading(false);
        return;
      }

      if (editingTx.isEdited) {
        const payload = {
          id: editingTx.id,
          application_number: editingTx.application_number,
          placement_fee_per: toNumber(editingTx.placement_fee_per),
          placement_fee_amount: toNumber(editingTx.placement_fee_amount),
          upfront_fee_per: toNumber(editingTx.upfront_fee_per),
          upfront_fee_amount: toNumber(editingTx.upfront_fee_amount),
          trail_per: toNumber(editingTx.trail_per),
          total_trail_amount: toNumber(editingTx.trail_amount),
          checker_name: checkerName,
          user_id: userId
        };

        const endpoint = editSource === 'rejected'
          ? "/transactionmanagement/re-edit-transaction"
          : "/transactionmanagement/edit-transaction";

        const response = await post(endpoint, payload);

        if (editSource === 'rejected') {
          // Update the rejected transactions list locally
          setRejectedTransactions(prev =>
            prev.map(tx =>
              tx.id === editingTx.id ? {
                ...tx,
                ...editingTx,
                checker_name: checkerName,
                is_edit: true
              } : tx
            )
          );
        } else if (isEditingError) {
          setErrorTransactions(prev =>
            prev.map(tx =>
              tx.id === editingTx.id ? {
                ...tx,
                ...editingTx,
                checker_name: checkerName,
                is_edit: true
              } : tx
            )
          );
        } else {
          setValidTransactions(prev =>
            prev.map(tx =>
              tx.id === editingTx.id ? {
                ...tx,
                ...editingTx,
                checker_name: checkerName,
                is_edit: true
              } : tx
            )
          );
        }

        alert("Transaction updated successfully!");
      } else {
        alert("No changes were made to the transaction.");
      }

      setEditingTx(null);
      setIsEditingError(false);
      setEditSource(null);
      setPlacementFeePercent("0");
      setSelectedAmcUser("");
    } catch (error) {
      console.error("Failed to update transaction:", error);
      alert(error.response?.data?.message || "Failed to update transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async (transaction, source) => {
    // For rejected transactions, we might allow release even if is_edit is false, depending on requirement.
    // Assuming same validation applies (must be Edited) unless user says otherwise.
    // However, if it's already "Rejected", and we "Edit" it, is_edit becomes true.

    // For valid transactions, we allow release even if not edited
    const canRelease = source === 'valid' || source === 'error' || transaction.is_edit;

    if (canRelease) {
      try {
        setLoading(true);
        // If it's a valid transaction (unedited), we might need a specific endpoint or just force edit status?
        // Based on conversation, "release button should be enabled everytime in case of valid transactions only"
        // And "unedited transactions are automatically approved... similar to /approveTransaction" ??
        // Actually, the previous /approveTransaction logic for valid files might be relevant.
        // But here we rely on the existing release endpoints.
        // If the backend expects `is_edit` to be true for specific endpoints, we might have issues.
        // However, usually "release" just finalizes it.
        // Let's assume the existing endpoints work for unedited if we don't block it here.

        const endpoint = source === 'rejected'
          ? `/transactionmanagement/re-release-transaction/${transaction.id}`
          : `/transactionmanagement/release-transaction/${transaction.id}`;

        const checkerName = transactionCheckers[transaction.id] || transaction.checker_name;

        await post(endpoint, {
          skipMakerChecker: true,
          checker_name: checkerName
        });
        alert("Transaction released successfully.");

        if (source === 'rejected') {
          setRejectedTransactions(prev => prev.filter(tx => tx.id !== transaction.id));
        } else if (source === 'error') {
          setErrorTransactions(prev => prev.filter(tx => tx.id !== transaction.id));
        } else {
          setValidTransactions(prev => prev.filter(tx => tx.id !== transaction.id));
        }
      } catch (error) {
        console.error("Failed to release transaction:", error);
        alert(error.response?.data?.Message || "Failed to release transaction");
      } finally {
        setLoading(false);
      }
    } else {
      alert("Transaction must be edited before releasing.");
    }
  };

  const validColumns = useMemo(
    () => [
      { Header: "Fund Name", accessor: "fund_name", size: 160 },
      { Header: "Class Code", accessor: "class_code", size: 160 },
      { Header: "APRN", accessor: "arn_code", size: 160 },
      { Header: "Distributor PAN", accessor: "distributor_pan", size: 180 },
      {
        Header: "Capital Commitment",
        accessor: "capital_commitment",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Placement Fee %",
        accessor: "placement_fee_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue}%`;
        },
      },
      {
        Header: "Placement Fee Amount",
        accessor: "placement_fee_amount",
        size: 220,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Upfront Fee %",
        accessor: "upfront_fee_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Upfront Fee Amount",
        accessor: "upfront_fee_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Trail Fee %",
        accessor: "trail_per",
        size: 150,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Total Trail Amount",
        accessor: "trail_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Checker Name",
        accessor: "checker_name",
        size: 180,
        Cell: ({ value, row }) => {
          const transactionId = row.original.id;
          const storedChecker = transactionCheckers[transactionId];
          return storedChecker || value || "-";
        },
      },

      {
        Header: "Management Fee Sharing %",
        accessor: "management_fee_sharing_per",
        size: 250,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Management Fee Amount",
        accessor: "management_fee_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Performance Fee Sharing %",
        accessor: "performance_fee_sharing_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Performance Fee Amount",
        accessor: "performance_fee_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        size: 150,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Actions",
        size: 380,
        Cell: ({ row }) => {
          const transaction = row.original;
          const transactionId = transaction.id;
          const currentChecker = transactionCheckers[transactionId] || transaction.checker_name || "";

          return (
            <div className="flex flex-col gap-2 py-1">
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handleEditClick(transaction, 'valid')}
                  className="bg-[#f3f4f6] border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-gray-200 text-xs font-semibold shadow-sm"
                >
                  <Edit size={14} className="text-gray-500" />
                  Edit
                </button>
                <button
                  onClick={() => handleRelease(transaction, 'valid')}
                  className="bg-[#6366f1] text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-[#4f46e5] text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={14} />
                  Release
                </button>
                <div className="relative flex-1 min-w-[140px]">
                  <select
                    onChange={(e) => handleCheckerChange(transactionId, e.target.value)}
                    value={currentChecker}
                    className="w-full appearance-none bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#6366f1] focus:border-transparent transition-all pr-8 shadow-sm cursor-pointer"
                  >
                    <option value="">Select Checker</option>
                    {amcUsers.map((user) => (
                      <option key={user.id} value={user.name || user.username}>
                        {user.name || user.username}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [amcUsers, loading, transactionCheckers]
  );

  const errorColumns = useMemo(
    () => [
      { Header: "Fund Name", accessor: "fund_name", size: 160 },
      { Header: "Class Code", accessor: "class_code", size: 160 },
      { Header: "APRN", accessor: "arn_code", size: 160 },
      { Header: "Distributor PAN", accessor: "distributor_pan", size: 180 },
      {
        Header: "Capital Commitment",
        accessor: "capital_commitment",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Placement Fee %",
        accessor: "placement_fee_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue}%`;
        },
      },
      {
        Header: "Placement Fee Amount",
        accessor: "placement_fee_amount",
        size: 220,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Upfront Fee %",
        accessor: "upfront_fee_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Upfront Fee Amount",
        accessor: "upfront_fee_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Trail Fee %",
        accessor: "trail_per",
        size: 150,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Total Trail Amount",
        accessor: "trail_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Checker Name",
        accessor: "checker_name",
        size: 180,
        Cell: ({ value, row }) => {
          const transactionId = row.original.id;
          const storedChecker = transactionCheckers[transactionId];
          return storedChecker || value || "-";
        },
      },
      {
        Header: "Error Reason",
        accessor: "status",
        size: 250,
        Cell: ({ value }) => (
          <div className="text-red-600 font-semibold whitespace-normal break-words leading-tight text-left min-w-[200px]">
            {value || "Validation Error"}
          </div>
        ),
      },
      {
        Header: "Actions",
        size: 380,
        Cell: ({ row }) => {
          const transaction = row.original;
          const transactionId = transaction.id;
          const currentChecker = transactionCheckers[transactionId] || transaction.checker_name || "";

          return (
            <div className="flex flex-col gap-2 py-1">
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handleEditClick(transaction, 'error')}
                  className="bg-[#f3f4f6] border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-gray-200 text-xs font-semibold shadow-sm"
                >
                  <Edit size={14} className="text-gray-500" />
                  Edit
                </button>
                <button
                  onClick={() => handleRelease(transaction, 'error')}
                  className="bg-[#6366f1] text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-[#4f46e5] text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save size={14} />
                  Release
                </button>
                <div className="relative flex-1 min-w-[140px]">
                  <select
                    onChange={(e) => handleCheckerChange(transactionId, e.target.value)}
                    value={currentChecker}
                    className="w-full appearance-none bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#6366f1] focus:border-transparent transition-all pr-8 shadow-sm cursor-pointer"
                  >
                    <option value="">Select Checker</option>
                    {amcUsers.map((user) => (
                      <option key={user.id} value={user.name || user.username}>
                        {user.name || user.username}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [amcUsers, loading, transactionCheckers]
  );

  const rejectedColumns = useMemo(
    () => [
      { Header: "Fund Name", accessor: "fund_name", size: 160 },
      { Header: "Class Code", accessor: "class_code", size: 160 },
      { Header: "APRN", accessor: "arn_code", size: 160 },
      { Header: "Distributor PAN", accessor: "distributor_pan", size: 180 },
      {
        Header: "Capital Commitment",
        accessor: "capital_commitment",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Placement Fee %",
        accessor: "placement_fee_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue}%`;
        },
      },
      {
        Header: "Placement Fee Amount",
        accessor: "placement_fee_amount",
        size: 220,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Upfront Fee %",
        accessor: "upfront_fee_per",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Upfront Fee Amount",
        accessor: "upfront_fee_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Trail Fee %",
        accessor: "trail_per",
        size: 150,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `${numValue.toFixed(2)}%`;
        },
      },
      {
        Header: "Total Trail Amount",
        accessor: "trail_amount",
        size: 200,
        Cell: ({ value }) => {
          const numValue = toNumber(value);
          return `₹${numValue.toLocaleString()}`;
        },
      },
      {
        Header: "Checker Name",
        accessor: "checker_name",
        size: 180,
        Cell: ({ value, row }) => {
          const transactionId = row.original.id;
          const storedChecker = transactionCheckers[transactionId];
          return storedChecker || value || "-";
        },
      },
      {
        Header: "Status",
        accessor: "status",
        size: 150,
        Cell: ({ value }) => (
          <span className="text-red-600 font-medium">
            Rejected
          </span>
        ),
      },
      {
        Header: "Actions",
        size: 380,
        Cell: ({ row }) => {
          const transaction = row.original;
          const transactionId = transaction.id;
          const currentChecker = transactionCheckers[transactionId] || transaction.checker_name || "";

          return (
            <div className="flex flex-col gap-2 py-1">
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handleEditClick(transaction, 'rejected')}
                  className="bg-[#f3f4f6] border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-gray-200 text-xs font-semibold shadow-sm"
                >
                  <Edit size={14} className="text-gray-500" />
                  Edit
                </button>
                <button
                  onClick={() => handleRelease(transaction, 'rejected')}
                  disabled={!transaction.is_edit}
                  className="bg-[#6366f1] text-white px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 hover:bg-[#4f46e5] text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!transaction.is_edit ? "Transaction must be edited to release" : ""}
                >
                  <Save size={14} />
                  Release
                </button>
                <div className="relative flex-1 min-w-[140px]">
                  <select
                    onChange={(e) => handleCheckerChange(transactionId, e.target.value)}
                    value={currentChecker}
                    className="w-full appearance-none bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#6366f1] focus:border-transparent transition-all pr-8 shadow-sm cursor-pointer"
                  >
                    <option value="">Select Checker</option>
                    {amcUsers.map((user) => (
                      <option key={user.id} value={user.name || user.username}>
                        {user.name || user.username}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [amcUsers, loading, transactionCheckers]
  );

  const renderEditDialog = () => {
    if (!editingTx) return null;

    if (isEditingError) {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capital Commitment
              </label>
              <InputText
                className="w-full px-2"
                value={`₹${toNumber(editingTx.capital_commitment).toLocaleString()}`}
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placement Fee %
              </label>
              <InputText
                className="w-full px-2"
                value={placementFeePercent}
                onChange={(e) => handlePlacementFeeChange(e.target.value)}
                placeholder="Enter percentage"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Calculated Fees</h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Placement Fee Amount
                </label>
                <InputText
                  className="w-full px-2"
                  value={`₹${toNumber(editingTx.placement_fee_amount).toLocaleString()}`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upfront Fee %
                </label>
                <InputText
                  className="w-full px-2"
                  value={`${toNumber(editingTx.upfront_fee_per).toFixed(2)}%`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upfront Fee Amount
                </label>
                <InputText
                  className="w-full px-2"
                  value={`₹${toNumber(editingTx.upfront_fee_amount).toLocaleString()}`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trail Fee %
                </label>
                <InputText
                  className="w-full px-2"
                  value={`${toNumber(editingTx.trail_per).toFixed(2)}%`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trail Fee Amount
                </label>
                <InputText
                  className="w-full px-2"
                  value={`₹${toNumber(editingTx.trail_amount).toLocaleString()}`}
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button
              onClick={() => {
                setEditingTx(null);
                setIsEditingError(false);
                setPlacementFeePercent("0");
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-1"
            >
              <Save size={16} />
              {loading ? "Saving..." : "Save Corrections"}
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capital Commitment
              </label>
              <InputText
                className="w-full px-2"
                value={`₹${toNumber(editingTx.capital_commitment).toLocaleString()}`}
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placement Fee %
              </label>
              <InputText
                className="w-full px-2"
                value={placementFeePercent}
                onChange={(e) => handlePlacementFeeChange(e.target.value)}
                placeholder="Enter percentage"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Calculated Fees</h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Placement Fee Amount
                </label>
                <InputText
                  className="w-full px-2"
                  value={`₹${toNumber(editingTx.placement_fee_amount).toLocaleString()}`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upfront Fee %
                </label>
                <InputText
                  className="w-full px-2"
                  value={`${toNumber(editingTx.upfront_fee_per).toFixed(2)}%`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upfront Fee Amount
                </label>
                <InputText
                  className="w-full px-2"
                  value={`₹${toNumber(editingTx.upfront_fee_amount).toLocaleString()}`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trail Fee %
                </label>
                <InputText
                  className="w-full px-2"
                  value={`${toNumber(editingTx.trail_per).toFixed(2)}%`}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trail Fee Amount
                </label>
                <InputText
                  className="w-full px-2"
                  value={`₹${toNumber(editingTx.trail_amount).toLocaleString()}`}
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button
              onClick={() => {
                setEditingTx(null);
                setIsEditingError(false);
                setPlacementFeePercent("0");
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-1"
            >
              <Save size={16} />
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div className="max-w-full mx-auto p-6 space-y-6 bg-[#f8f9fa] min-h-screen">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold text-gray-800">Transaction Management</h1>

          <div className="flex gap-4 items-center">
            <Button
              onClick={processKfinFile}
              disabled={loading}
              className="bg-[#6366f1] hover:bg-[#4f46e5] text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm flex items-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Loading...
                </>
              ) : (
                "Refresh All"
              )}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
          <Tabs
            selectedIndex={selectedTab === "valid" ? 0 : selectedTab === "errors" ? 1 : 2}
            onSelect={(index) => setSelectedTab(index === 0 ? "valid" : index === 1 ? "errors" : "rejected")}
            className="w-full"
          >
            <TabList className="flex border-b border-gray-200 bg-white">
              <Tab
                className={`px-6 py-4 cursor-pointer text-sm font-semibold transition-all duration-200 outline-none ${selectedTab === "valid"
                  ? "bg-[#6366f1] text-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Valid Transactions ({validTransactions.length})
              </Tab>

              <Tab
                className={`px-6 py-4 cursor-pointer text-sm font-semibold transition-all duration-200 outline-none ${selectedTab === "errors"
                  ? "bg-[#6366f1] text-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Error Records ({errorTransactions.length})
              </Tab>

              <Tab
                className={`px-6 py-4 cursor-pointer text-sm font-semibold transition-all duration-200 outline-none ${selectedTab === "rejected"
                  ? "bg-[#6366f1] text-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Rejected Records ({rejectedTransactions.length})
              </Tab>
            </TabList>

            <div className="p-0">
              <TabPanel>
                {validTransactions.length > 0 ? (
                  <div className="p-4">
                    <ReusableTable columns={validColumns} data={validTransactions} />
                  </div>
                ) : (
                  <div className="py-20 text-center flex flex-col items-center justify-center text-gray-400">
                    <div className="mb-2 opacity-20">
                      <Save size={48} />
                    </div>
                    No valid transactions found.
                  </div>
                )}
              </TabPanel>

              <TabPanel>
                {errorTransactions.length > 0 ? (
                  <div className="p-4">
                    <ReusableTable columns={errorColumns} data={errorTransactions} />
                  </div>
                ) : (
                  <div className="py-20 text-center flex flex-col items-center justify-center text-gray-400">
                    <div className="mb-2 opacity-20">
                      <Save size={48} />
                    </div>
                    No error records found.
                  </div>
                )}
              </TabPanel>

              <TabPanel>
                {rejectedTransactions.length > 0 ? (
                  <div className="p-4">
                    <ReusableTable columns={rejectedColumns} data={rejectedTransactions} />
                  </div>
                ) : (
                  <div className="py-20 text-center flex flex-col items-center justify-center text-gray-400">
                    <div className="mb-2 opacity-20">
                      <Save size={48} />
                    </div>
                    No rejected records found.
                  </div>
                )}
              </TabPanel>
            </div>
          </Tabs>
        </div>

        {/* Valid Transaction Edit Dialog */}
        <CustomDialog
          visible={!!editingTx && !isEditingError}
          header={`Edit Valid Transaction - ${editingTx?.application_number || ""}`}
          width="50vw"
          onHide={() => {
            setEditingTx(null);
            setIsEditingError(false);
            setPlacementFeePercent("0");
          }}
        >
          {!isEditingError && renderEditDialog()}
        </CustomDialog>

        {/* Error Transaction Edit Dialog */}
        <CustomDialog
          visible={!!editingTx && isEditingError}
          header={`Correct Error Transaction - ${editingTx?.application_number || ""}`}
          width="50vw"
          onHide={() => {
            setEditingTx(null);
            setIsEditingError(false);
            setPlacementFeePercent("0");
          }}
        >
          {isEditingError && renderEditDialog()}
        </CustomDialog>
      </div>
    </>
  );
};

export default TransactionManagement;